import clsx from 'clsx'
import { observer } from 'mobx-react'
import type { PropsWithChildren } from 'react'
import type { Notification, NotificationCTAOption } from 'src/dto'

import { Icon } from '@/component/atoms/Icon'
import type { IconType } from '@/component/atoms/Icon/Icon'

import S from './Notification.style'

type Props = Pick<Notification, 'isRead' | 'formatToNotificationDatetime' | 'message'> & {
  ctaOptions: NotificationCTAOption[]
  iconOption: { name: IconType; color?: string; size?: number }
  title1?: string
  title2: string
}
type DefaultCTAProps = PropsWithChildren<Pick<Props, 'isRead' | 'ctaOptions'>>
type SecondCTAProps = { ctaOption: Props['ctaOptions'][number] }

const NotificationItem = ({
  isRead,
  message,
  title1,
  title2,
  ctaOptions,
  iconOption,
  formatToNotificationDatetime,
}: Props) => {
  const CombinedTitle = () => {
    if (!title1) {
      return <>{title2}</>
    }
    return (
      <>
        {title1} | {title2}
      </>
    )
  }

  return (
    <DefaultCTA isRead={isRead} ctaOptions={ctaOptions}>
      <div className="icon-col">
        <Icon {...iconOption} />
      </div>
      <div className="content-col">
        {(title1 || title2) && <div className="title-row">{CombinedTitle()}</div>}
        <div className="main-row">{message}</div>
        <div className="sub-row">
          <span>{formatToNotificationDatetime}</span>
          <SecondCTA ctaOption={ctaOptions[1]} />
        </div>
      </div>
    </DefaultCTA>
  )
}

const DefaultCTA = observer(({ isRead, ctaOptions, children }: DefaultCTAProps) => {
  if (!ctaOptions || ctaOptions.length === 0) {
    return <></>
  }

  const withoutOtherCTA = ctaOptions.length === 1
  const { className, onCTAClick } = ctaOptions[0]

  return (
    <S.Item
      className={clsx(isRead && 'read', withoutOtherCTA && 'without-cta', className)}
      onClick={onCTAClick}
    >
      {children}
    </S.Item>
  )
})

const SecondCTA = observer(({ ctaOption }: SecondCTAProps) => {
  if (!ctaOption) {
    return <></>
  }

  const { text, className, onCTAClick } = ctaOption

  return (
    <button className={clsx('second-cta', className)} onClick={onCTAClick}>
      <p>{text}</p>
    </button>
  )
})

export default observer(NotificationItem)
